import {takeWhile, catchError} from 'rxjs/operators';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgZone} from '@angular/core';
import {DetailControlOption, ValuesObject} from '../detail-tabconfig';
import {ResourceService} from '../../resource.service';
import {FilterResult} from '../../filterresult';
import {AbstractControlComponent} from './abstract-control';
import {Observable} from 'rxjs';
import {DetailService} from '../detail.service';
import {CacheService} from '../../../cache/cache.service';
import {SupportedLocaleConfiguration} from '../../../cache/uiconfiguration/supportedlocaleconfiguration';
import {ViewTabComponent} from '../viewtab.component';
import {optioncontrolRequired} from './validators/optioncontrol-required-validator';
import {ObjectTypeDescription} from '../../../cache/schema/objecttypedescription';
import {InvalidItemSourceObjectError} from '../../../error/errordefinitions';
import {ErrorService} from '../../../error/error.service';
import {DisplayAttribute} from '../../displayattribute';
import {EnvironmentConfigService} from '../../../core/services/environment-config.service';
import {CtrlDropdownComponent} from './ctrl-dropdown.component';

@Component({
  selector: 'ctrl-multiselect',
  template: `
    <mat-form-field floatLabel="never">
      <mat-select
        placeholder="{{ (getCaughtObservable(_config?.Caption) | async)?.Values }}"
        [formControl]="formControl"
        (opened)="onSelectOpen()"
        (closed)="onSelectClose()"
        multiple
      >
        @for (opt of options; track opt) {
          <span>
            @if (getCaughtObservable(opt.Value) | async) {
              <mat-option
                matTooltipShowDelay="300"
                [matTooltip]="(getCaughtObservable(opt.Hint) | async)?.Values"
                [value]="(getCaughtObservable(opt.Value) | async)?.Values"
              >
                {{ (getCaughtObservable(opt.Caption) | async)?.Values }}
              </mat-option>
            }
          </span>
        }
      </mat-select>
    </mat-form-field>
    @if (
      (getCaughtObservable(isEnabled) | async) &&
      formControl.touched &&
      formControl.hasError('required')
    ) {
      <mat-error>
        <language key="ControlErrorRequired" defaultValue="This field is required."></language>
      </mat-error>
    }
  `,
  styles: [
    `
      mat-select,
      mat-form-field {
        width: 100%;
      } /* TODO(mdc-migration): The following rule targets internal classes of form-field that may no longer apply for the MDC version. */
      :host ::ng-deep .mat-form-field-infix {
        border-top: none;
      } /* TODO(mdc-migration): The following rule targets internal classes of form-field that may no longer apply for the MDC version. */
      :host ::ng-deep .mat-form-field-wrapper {
        padding-bottom: 0;
      } /* TODO(mdc-migration): The following rule targets internal classes of form-field that may no longer apply for the MDC version. */
      :host ::ng-deep .mat-form-field-underline {
        bottom: 0;
      } /* TODO(mdc-migration): The following rule targets internal classes of select that may no longer apply for the MDC version. */
      :host ::ng-deep .mat-select-value {
        vertical-align: middle;
      } /* TODO(mdc-migration): The following rule targets internal classes of select that may no longer apply for the MDC version. */
      :host ::ng-deep .mat-select-trigger {
        height: 20px;
        font-size: inherit;
      }
      :host ::ng-deep :host ::ng-deep .mat-floating-placeholder {
        pointer-events: none;
      }
      ::ng-deep .mat-pseudo-checkbox-checked::after {
        box-sizing: content-box;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CtrlMultiselectComponent extends AbstractControlComponent {
  constructor(
    parent: ViewTabComponent,
    private resourceService: ResourceService,
    changeDetector: ChangeDetectorRef,
    private cacheService: CacheService,
    _ngZone: NgZone,
    errorService: ErrorService,
    private envConfig: EnvironmentConfigService,
  ) {
    super(parent, 'SelectedValue', 'SelectedValue', changeDetector, _ngZone, errorService);
  }

  options: DetailControlOption[] = [];
  private _objectType: string = null;
  @Input()
  set objectType(objectType: string) {
    this._objectType = objectType;
  }

  get objectType(): string {
    return this._objectType;
  }

  protected initValidators(): void {
    if (this._config == null || this.formControl == null) {
      throw Error('Initialized target too early.');
    }

    this._config
      .getBooleanPropertyValues('Required')
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        takeWhile(() => this.alive),
      )
      .subscribe(required => {
        if (required === true) {
          this.formControl.setValidators([optioncontrolRequired()]);
        } else {
          this.formControl.clearValidators();
        }
        this.formControl.updateValueAndValidity({emitEvent: false});
      });
    CtrlDropdownComponent.regenerateOptions(
      this._config,
      this.resourceService,
      this.cacheService,
      this.objectType,
      this.envConfig,
    )
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        takeWhile(() => this.alive),
      )
      .subscribe(options => {
        this.options = options;
        this.changeDetector.markForCheck();
      });
  }

  protected formatSource(source: any): any {
    if (source === '' || source == null) source = [];
    return source;
  }

  protected formatTarget(value: any): any {
    if (Array.isArray(value) && value.length === 0) value = null;
    return value;
  }

  onSelectOpen(): void {
    document.body.style.overflow = 'hidden';
  }

  onSelectClose(): void {
    document.body.style.overflow = '';
  }
}
